import React, { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

const GOLD = '#B8860B';
const NAVY = '#050a0f';

export default function AdminGlobalBroadcast() {
  const [message, setMessage] = useState('');
  const [target, setTarget] = useState('all'); // all, stylists, clients
  const [isUrgent, setIsUrgent] = useState(false);
  const [loading, setLoading] = useState(false);

  // EMERGENCY ACTION: LOCK THE SYSTEM
  const triggerMaintenance = async () => {
    const confirmLock = window.confirm(
      "CRITICAL ACTION: This will redirect all non-admin users to the Maintenance Page immediately. Continue?"
    );
    if (!confirmLock) return;

    setLoading(true);
    try {
      await addDoc(collection(db, 'system_alerts'), {
        sender: 'ADMIN',
        message: "The system is undergoing scheduled maintenance. We'll be back shortly.",
        target: 'all',
        type: 'MAINTENANCE_LOCK',
        priority: 'high',
        createdAt: serverTimestamp(),
      });
      alert("MAINTENANCE MODE ACTIVATED GLOBALLY");
    } catch (err) {
      console.error(err);
      alert("Failed to trigger maintenance lock.");
    } finally {
      setLoading(false);
    }
  };

  // STANDARD ACTION: SEND NOTIFICATION
  const handleSystemBlast = async () => {
    if (!message) return alert("Message cannot be empty.");
    
    setLoading(true);
    try {
      await addDoc(collection(db, 'system_alerts'), {
        sender: 'ADMIN',
        message,
        target,
        type: 'SYSTEM_WIDE',
        priority: isUrgent ? 'high' : 'normal',
        createdAt: serverTimestamp(),
      });
      alert(`Blast sent to ${target} successfully!`);
      setMessage('');
      setIsUrgent(false);
    } catch (err) {
      console.error(err);
      alert("Error sending global alert.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={pageContainer}>
      <header style={headerStyle}>
        <h2 style={{ color: GOLD, margin: 0, letterSpacing: '2px' }}>SYSTEM CONTROL CENTER</h2>
        <p style={{ opacity: 0.6, fontSize: '0.85rem' }}>Real-time platform authority & broadcasts</p>
      </header>

      {/* EMERGENCY SECTION */}
      <div style={card}>
        <h3 style={{ color: '#ff4d4d', fontSize: '0.9rem', marginTop: 0 }}>Emergency Protocols</h3>
        <p style={{ fontSize: '0.75rem', opacity: 0.6, marginBottom: '15px' }}>
          Activating Maintenance Lock will forcefully redirect users to the maintenance landing page.
        </p>
        <button 
          onClick={triggerMaintenance} 
          disabled={loading} 
          style={maintBtn}
        >
          {loading ? 'PROCESSING...' : 'ACTIVATE MAINTENANCE LOCK'}
        </button>
      </div>

      {/* BROADCAST SECTION */}
      <div style={card}>
        <h3 style={{ color: GOLD, fontSize: '0.9rem', marginTop: 0 }}>Global Broadcast Dispatch</h3>
        
        <div style={fieldGroup}>
          <label style={labelStyle}>Target Audience</label>
          <select 
            value={target} 
            onChange={(e) => setTarget(e.target.value)} 
            style={selectStyle}
          >
            <option value="all">Everyone (All Users)</option>
            <option value="stylists">Stylists Only</option>
            <option value="clients">Clients Only</option>
          </select>
        </div>

        <div style={fieldGroup}>
          <label style={labelStyle}>Alert Message</label>
          <textarea 
            maxLength={250}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Enter system alert message (Max 250 chars)..."
            style={inputStyle}
          />
        </div>
        
        <div style={checkboxContainer}>
          <input 
            type="checkbox" 
            id="urgent"
            checked={isUrgent} 
            onChange={(e) => setIsUrgent(e.target.checked)} 
            style={checkboxStyle}
          />
          <label htmlFor="urgent" style={{ fontSize: '0.85rem', cursor: 'pointer' }}>
            Mark as High Priority (Red UI Elements)
          </label>
        </div>
        
        <button 
          onClick={handleSystemBlast} 
          disabled={loading || !message} 
          style={sendBtn}
        >
          {loading ? 'DISPATCHING...' : 'BLAST SYSTEM ALERT'}
        </button>
      </div>
    </div>
  );
}

// STYLES
const pageContainer = { 
  padding: '40px', 
  background: NAVY, 
  minHeight: '100vh', 
  color: 'white',
  fontFamily: 'inherit'
};

const headerStyle = {
  marginBottom: '30px',
  borderBottom: '1px solid rgba(184, 134, 11, 0.2)',
  paddingBottom: '20px'
};

const card = { 
  background: 'rgba(255,255,255,0.03)', 
  padding: '25px', 
  borderRadius: '24px', 
  border: '1px solid rgba(255,255,255,0.05)',
  marginBottom: '20px',
  maxWidth: '600px'
};

const fieldGroup = {
  marginBottom: '20px'
};

const labelStyle = {
  display: 'block',
  fontSize: '0.7rem',
  color: GOLD,
  textTransform: 'uppercase',
  fontWeight: 'bold',
  marginBottom: '8px',
  letterSpacing: '1px'
};

const selectStyle = { 
  width: '100%', 
  padding: '12px', 
  background: '#000', 
  color: GOLD, 
  border: '1px solid #333', 
  borderRadius: '12px',
  outline: 'none'
};

const inputStyle = { 
  width: '100%', 
  height: '120px', 
  background: '#000', 
  color: 'white', 
  border: '1px solid #333', 
  borderRadius: '12px', 
  padding: '15px', 
  boxSizing: 'border-box',
  resize: 'none',
  fontSize: '0.9rem',
  outline: 'none'
};

const checkboxContainer = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  marginBottom: '25px',
  padding: '10px',
  background: 'rgba(255,255,255,0.02)',
  borderRadius: '8px'
};

const checkboxStyle = {
  accentColor: GOLD,
  width: '18px',
  height: '18px'
};

const sendBtn = { 
  width: '100%', 
  padding: '16px', 
  background: GOLD, 
  color: 'black', 
  border: 'none', 
  borderRadius: '50px', 
  fontWeight: '800', 
  cursor: 'pointer',
  transition: '0.3s ease',
  fontSize: '0.85rem',
  letterSpacing: '1px'
};

const maintBtn = { 
  width: '100%', 
  padding: '14px', 
  background: 'transparent', 
  border: '1px solid #ff4d4d', 
  color: '#ff4d4d', 
  borderRadius: '50px', 
  fontWeight: 'bold', 
  cursor: 'pointer',
  fontSize: '0.8rem'
};
